import { TranslateService } from '@ngx-translate/core';
import { ConfigInstanceToken } from '../../interfaces/config.interface';
import * as _ from 'lodash';
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { CustomInjector } from '../../services/custom-injector/custom-injector';
import { NotificationDataInstanceToken } from '../../interfaces/notification-data.interface';
import { ValueHelperInstanceToken } from '../../interfaces/value-helper.interface';
import { ODataNotificationIdentifierOutModel } from '../low-level/notification-identifier.model';
import { NotificationModel } from '../low-level/notification.model';
import { DateModelOptions } from '../options/date-model-options';
import { TimeModelOptions } from '../options/time-model-options';
import { DateModel } from '../primitive/date.model';
import { TextModel } from '../primitive/text.model';
import { TimeModel } from '../primitive/time.model';
import { CompositeModel } from './composite.model';
import { ODataTimeZoneOutModel } from './time-zone.model';
import { TIMERECORDING_TYPE_OF_WORK_CODES } from './time-recording-entry.model';

export class TravelStatusUpdateModel extends CompositeModel {
    // ODat a models
    public mType: TextModel;
    public date: DateModel;
    public time: TimeModel;
    public endDate: DateModel;
    public endTime: TimeModel;

    // Vari ables used in markup
    public startMoment: moment.Moment;
    public endMoment: moment.Moment;
    public statusLabel: string;
    public isCurrentStatusDISP: boolean;

    // inte rnally used variables
    private plannedDurationInMinutes: number;

    // exte rnally used info
    public escalated: boolean;
    public salesOrg: TextModel;
    public plant: TextModel;
    public constructor(
        public notification: NotificationModel,
        public nextStatus: NOTIFICATION_STATUS_CODE,
    ) {
        super('TravelStatusUpdateModel');

        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);
        const configService = injector.get(ConfigInstanceToken);
        const notificationService = injector.get(NotificationDataInstanceToken);
        const valueHelperService = injector.get(ValueHelperInstanceToken);

        const status = notificationService.getStatus(notification);

        this.isCurrentStatusDISP = status.statusCode === NOTIFICATION_STATUS_CODE.DISP;

        this.mType = <TextModel>this.set(new TextModel('MessageType', nextStatus.toString()));

        const plannedStart = notificationService.getNotificationStart(notification);
        const plannedEnd = notificationService.getNotificationEnd(notification);

        // If c urrent status is not DISP then set date and time to current
        this.startMoment =
            this.nextStatus === NOTIFICATION_STATUS_CODE.ETA
                ? plannedStart.clone().startOf('minute')
                : moment().startOf('minute');

        // get  start time of TRAV and use it as minimum for OSR
        const travStatus = notificationService.getNotificationStatusByStatusType(
            notification,
            NOTIFICATION_STATUS_CODE.TRAV,
        );
        const travStartMoment = travStatus ? travStatus.startTime.clone().startOf('minute') : undefined;

        const now = moment().startOf('minute');

        const travLimitPast: moment.Duration | undefined = undefined;

        // ETA: +/- 3h (configurable)
        const etaLimitPast = moment.duration(configService.getEtaLimitMinutes(), 'minutes');
        const etaLimitFuture = etaLimitPast;
        // TRAV : Can be later today or at any time in the past
        const travLimitFuture = moment.duration(moment().endOf('day').diff(this.startMoment));
        // OSR: needs to happen at the same time or after TRAV
        // for  P58 max limit of OSR is tomorrow end of day
        // only limit OSR time if we have a TRAV status to refer to. Not having a locally stored TRAV status can
        // eith er happen when the app was deleted and the notification is synced from the backend in status TRAV
        // or i f we omit TRAV and jump directly to OSR
        const osrLimitPast = travStartMoment ? moment.duration(now.diff(travStartMoment)) : undefined;
        const osrLimitFuture = moment().add(1, 'day').endOf('day');

        // Limi t options to change date and time based on the requested status:
        let timeModelOptions = new TimeModelOptions();
        let dateModelOptions = new DateModelOptions();
        switch (nextStatus) {
            case NOTIFICATION_STATUS_CODE.ETA:
                timeModelOptions = new TimeModelOptions({
                    ...timeModelOptions,
                    minValue: etaLimitPast
                        ? this.startMoment.clone().subtract(etaLimitPast)
                        : timeModelOptions.minValue,
                    maxValue: etaLimitFuture ? this.startMoment.clone().add(etaLimitFuture) : timeModelOptions.maxValue,
                });

                dateModelOptions = new DateModelOptions({
                    ...dateModelOptions,
                    minValue: etaLimitPast
                        ? this.startMoment.clone().subtract(etaLimitPast)
                        : dateModelOptions.minValue,
                    maxValue: etaLimitFuture ? this.startMoment.clone().add(etaLimitFuture) : dateModelOptions.maxValue,
                });
                break;
            case NOTIFICATION_STATUS_CODE.TRAV:
                timeModelOptions = new TimeModelOptions({
                    ...timeModelOptions,
                    minValue: travLimitPast
                        ? this.startMoment.clone().subtract(travLimitPast)
                        : timeModelOptions.minValue,
                    maxValue: travLimitFuture
                        ? this.startMoment.clone().add(travLimitFuture)
                        : timeModelOptions.maxValue,
                });
                dateModelOptions = new DateModelOptions({
                    ...dateModelOptions,
                    minValue: travLimitPast
                        ? this.startMoment.clone().subtract(travLimitPast)
                        : dateModelOptions.minValue,
                    maxValue: travLimitFuture
                        ? this.startMoment.clone().add(travLimitFuture)
                        : dateModelOptions.maxValue,
                });
                break;
            case NOTIFICATION_STATUS_CODE.OSR:
                timeModelOptions = new TimeModelOptions({
                    ...timeModelOptions,
                    minValue: osrLimitPast
                        ? this.startMoment.clone().subtract(osrLimitPast)
                        : timeModelOptions.minValue,
                    maxValue: osrLimitFuture ?? timeModelOptions.maxValue,
                });

                dateModelOptions = new DateModelOptions({
                    ...dateModelOptions,
                    minValue: osrLimitPast
                        ? this.startMoment.clone().subtract(osrLimitPast)
                        : dateModelOptions.minValue,
                    maxValue: osrLimitFuture ?? dateModelOptions.maxValue,
                });
                break;
            case NOTIFICATION_STATUS_CODE.ROSR:
            case NOTIFICATION_STATUS_CODE.FCC:
                timeModelOptions = new TimeModelOptions({
                    ...timeModelOptions,
                    maxValue: osrLimitFuture ?? timeModelOptions.maxValue,
                });
                dateModelOptions = new DateModelOptions({
                    ...dateModelOptions,
                    maxValue: osrLimitFuture ?? dateModelOptions.maxValue,
                });
                break;
        }
        this.date = <DateModel>this.set(new DateModel('Date', this.startMoment, dateModelOptions));
        this.time = <TimeModel>this.set(new TimeModel('Time', this.startMoment, timeModelOptions));

        this.plannedDurationInMinutes = moment.duration(plannedEnd.diff(plannedStart)).asMinutes();
        this.endMoment = this.startMoment.clone().add(this.plannedDurationInMinutes, 'minutes');
        this.endDate = new DateModel('EndDate', this.endMoment, new DateModelOptions({ readonly: true }));
        this.endTime = new TimeModel('EndTime', this.endMoment, new TimeModelOptions({ readonly: true }));

        this.statusLabel = <string>translate.instant('NOTIFICATION_STATUS_' + nextStatus.toString() + '_LABEL');
        if (nextStatus === NOTIFICATION_STATUS_CODE.FCC || nextStatus === NOTIFICATION_STATUS_CODE.ROSR) {
            const timeRecordingTasks = valueHelperService.getTimeRecordingTaskTypeInfo();
            const statusValueHelper = timeRecordingTasks.find((value) => value.code === nextStatus);
            const isRosnOrFccnTaskAvailable = timeRecordingTasks.some(
                (value) =>
                    value.code === TIMERECORDING_TYPE_OF_WORK_CODES.ROSN ||
                    value.code === TIMERECORDING_TYPE_OF_WORK_CODES.FCCN,
            );
            if (!isRosnOrFccnTaskAvailable && statusValueHelper) {
                this.statusLabel = statusValueHelper.descText;
            }
        }
    }

    public getOutModel(): ODataTravelStatusUpdateOutModel {
        // crea te outmodel
        const outModel = <ODataTravelStatusUpdateOutModel>super.getOutModel();

        // merg e additional properties into outmodel
        _.merge(outModel, this.notification.identifier.getOutModel());
        _.merge(outModel, this.notification.timeZone.getOutModel());

        return outModel;
    }

    public updateEndMoment(): void {
        // upda te existing reference that is being used by endDate/endTime
        const newEndMoment = this.startMoment.clone().add(this.plannedDurationInMinutes, 'minutes');
        this.endMoment.set(newEndMoment.toObject());
        this.endDate.value.set(newEndMoment.toObject());
        this.endDate = cloneDeep(this.endDate); // make a clone deep to create reference, for triggering the onChanges hook
        this.endTime.value.set(newEndMoment.toObject());
        this.endTime = cloneDeep(this.endTime); // make a clone deep to create reference, for triggering the onChanges hook
    }

    public nextStatusETA(): boolean {
        return this.nextStatus === NOTIFICATION_STATUS_CODE.ETA;
    }
}

export interface ODataTravelStatusUpdateOutModel extends ODataNotificationIdentifierOutModel, ODataTimeZoneOutModel {
    MessageType: string;
    Date: string;
    Time: string;
}
